import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import FeatherIcon from 'feather-icons-react';
import { OrderItemsDoc, RETURN_ORDER_STATUS } from 'khufu-legos';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  Nav,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { createReturn, getOrder } from '../../apis/queries';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { CART_KEY } from '../../utils/tenant';
import DetailNotFound from '../DetailNotFound';
import { ORDERS_QUERY_KEY } from '../Orders/Orders';
import { RETURN_ORDERS_QUERY_KEY } from './ReturnOrders';

type CartItem = OrderItemsDoc;
type CartState = { [key: string]: CartItem };

const CreateReturns = () => {
  const { id } = useParams();

  if (!id) {
    return <DetailNotFound />;
  }

  return <CreateReturnsContent id={id} />;
};

const CreateReturnsContent = ({ id }: { id: string }) => {
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const queryClient = useQueryClient();
  const ORDER_ID_QUERY_KEY = ['orders', { id }];

  const savedCart = localStorage.getItem(CART_KEY);
  const prevSavedCart: CartState = savedCart ? JSON.parse(savedCart) : null;
  const [cart, setCart] = useState(prevSavedCart ?? {});

  const { data, isLoading } = useQuery({
    queryKey: ORDER_ID_QUERY_KEY,
    queryFn: () => getOrder(id)
  });

  useEffect(() => {
    // Reset cart when a new order is selected
    setCart({});
    localStorage.removeItem(CART_KEY);
  }, [id]);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const mutation = useMutation({
    mutationFn: (data: { orderId: string; reason: string; items: { orderItemId: string; returnQty: number }[] }) =>
      createReturn(data.orderId, data.reason, data.items),
    onSuccess: () => {
      setShowConfirmation(true);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDERS_QUERY_KEY, RETURN_ORDERS_QUERY_KEY]
      });
    }
  });

  const orders = data?.data;

  // Quantity already returned per product across this order's earlier returns, so a
  // second return cannot send back more than what is still with the customer.
  const alreadyReturned = (orders?.returns ?? [])
    .filter(orderReturn => orderReturn.status !== RETURN_ORDER_STATUS.CANCELED)
    .flatMap(orderReturn => orderReturn.items)
    .reduce<Record<string, number>>((acc, item) => {
      const productId = item.productId.toString();
      acc[productId] = (acc[productId] ?? 0) + item.quantity;
      return acc;
    }, {});

  const returnableQuantity = (item: OrderItemsDoc) =>
    Math.max(0, item.quantity - (alreadyReturned[item.productId.toString()] ?? 0));

  const handleCreateReturn = () => {
    if (!orders || !orders._id) return;

    const items = Object.values(cart).map(item => ({
      orderItemId: item._id.toString(),
      returnQty: item.quantity
    }));

    if (items.length === 0) {
      // Handle case where no items are selected
      alert('Please select at least one item to return.');
      return;
    }

    const orderData = {
      orderId: orders._id.toString(),
      reason,
      items: items
    };

    mutation.mutate(orderData);
  };

  const mutationLoading = mutation?.isPending;

  const handleCloseConfirmation = () => {
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setShowConfirmation(false);
      navigate('/return-orders/all');
    }, 2000);
  };

  const handleQuantityChange = (id: string, value: number) => {
    if (!orders) return;
    const originalOrder = orders.items.find(order => order._id.toString() === id);

    setCart(prevCart => {
      const order = prevCart[id] || originalOrder;
      if (!order) return prevCart;

      const maxQuantity = originalOrder ? returnableQuantity(originalOrder) : 0;

      const newQuantity = Math.min(value, maxQuantity);

      if (newQuantity === 0) {
        const newCart = { ...prevCart };
        delete newCart[id];
        return newCart;
      }

      const updatedCart = { ...prevCart, [id]: { ...order, quantity: newQuantity } };
      return updatedCart;
    });
  };

  const incrementQuantity = (id: string) => {
    handleQuantityChange(id, (cart[id]?.quantity || 0) + 1);
  };

  const decrementQuantity = (id: string) => {
    handleQuantityChange(id, (cart[id]?.quantity || 0) - 1);
  };

  const handleRemoveItem = (id: string) => {
    setCart(prevCart => {
      const newCart = { ...prevCart };
      delete newCart[id];
      return newCart;
    });
  };

  const totalProductsInCart = Object.keys(cart).length;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="main-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12} lg={12} xl={12} className="py-6">
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={6} className="text-center">
                <h6 className="mb-4 text-uppercase text-muted">Not Satisfied?</h6>

                <h1 className="my-4">Return items</h1>
                <p className="mb-5 text-muted">Select items to be returned</p>
              </Col>
            </Row>

            <Row>
              <Col xs={12} xl={8}>
                <Card>
                  <Card.Header>
                    <h4 className="card-header-title">Items</h4>
                  </Card.Header>
                  <Table size="sm" className="card-table table-nowrap" responsive>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Stock</th>
                        <th>Price</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="fs-base">
                      {orders?.items.map(order => {
                        const returnable = returnableQuantity(order);

                        return (
                          <tr key={order._id.toString()}>
                            <td>{order.name}</td>
                            <td>{returnable}</td>
                            <td>{order.price.toLocaleString()}</td>
                            <td>
                              <InputGroup size="sm" className="flex-nowrap">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => decrementQuantity(order._id.toString())}
                                  disabled={(cart[order._id.toString()]?.quantity || 0) <= 0}
                                >
                                  -
                                </Button>
                                <FormControl
                                  size="sm"
                                  type="number"
                                  value={cart[order._id.toString()]?.quantity || ''}
                                  onChange={e =>
                                    handleQuantityChange(order._id.toString(), parseInt(e.target.value) || 0)
                                  }
                                  min="0"
                                  max={returnable}
                                  disabled={returnable === 0}
                                  style={{ minWidth: '50px' }}
                                />
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => incrementQuantity(order._id.toString())}
                                  disabled={(cart[order._id.toString()]?.quantity || 0) >= returnable}
                                >
                                  +
                                </Button>
                              </InputGroup>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card>
              </Col>
              <Col xs={12} xl={4}>
                <Card>
                  <Card.Header>
                    <h4 className="card-header-title">Cart</h4>
                    <div className="cart-container d-flex align-items-center position-relative">
                      <FeatherIcon icon="shopping-cart" size="26" />
                      <Badge bg="primary" className="position-absolute" style={{ top: '9px', right: '-8px' }}>
                        {totalProductsInCart}
                      </Badge>
                    </div>
                  </Card.Header>
                  {Object.keys(cart).length === 0 ? (
                    <Card.Body className="text-center">
                      <EmptyTableState message="No items in the cart yet" />
                    </Card.Body>
                  ) : (
                    <Table size="sm" className="card-table table-nowrap" responsive>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th></th>
                        </tr>
                      </thead>
                      {Object.keys(cart).length && (
                        <tbody className="fs-base">
                          {Object.values(cart).map(item => (
                            <tr key={item._id.toString()}>
                              <td>{item.name}</td>
                              <td>{item.quantity}</td>
                              <td>{item.price.toLocaleString()}</td>
                              <td>
                                <span
                                  role="button"
                                  onClick={() => handleRemoveItem(item._id.toString())}
                                  style={{
                                    cursor: 'pointer',
                                    color: 'red'
                                  }}
                                >
                                  <FeatherIcon icon="x" size="14" />
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      )}
                    </Table>
                  )}
                </Card>

                <Form.Group className="mb-3">
                  <Form.Label>Reason for Return</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Enter the reason for return..."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Nav className="row align-items-center justify-content-between">
              <Col xs="auto">
                <Button variant="white" size="lg" onClick={() => navigate(`/order/${id}`)}>
                  Back
                </Button>
              </Col>
              <Col xs="auto">
                <Button variant="primary" size="lg" onClick={handleCreateReturn} disabled={mutationLoading}>
                  {mutationLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Returning...
                    </>
                  ) : (
                    'Create Return'
                  )}
                </Button>
              </Col>
            </Nav>
          </Col>
        </Row>
      </Container>

      <ConfirmationModal
        show={showConfirmation}
        onHide={handleCloseConfirmation}
        title="Return Order Confirmation"
        bodyContent={
          <div>
            <img src="/img/kanban/confirmation.jpg" alt="confirmation" style={{ width: '150px' }} />
            <p>Order has been returned successfully!</p>
          </div>
        }
        footerButtonText="Back to Return Order"
        loading={loading}
      />
    </div>
  );
};

export default CreateReturns;
